import { useTheme, useMediaQuery, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  TextField,
  Box,
  Snackbar,
  Alert,
  Typography,
  Select,
  FormControl,
  FormHelperText,
} from "@mui/material";
import dayjs from "dayjs";
import {
  alphanumericWithSpaceValidation,
  validateField,
} from "../../common/helpers/validation";
import {
  createDriverInfo as createDriverInfoAction,
  getAllDriverInfo,
  updateDriverInfo,
} from "../../redux/actions/driverInfoActions";
import { CircleOutlined } from "@mui/icons-material";
import {
  CommonYesNo,
  DriverType,
  Fileintype,
  RelationshipToInsured,
  genderList,
  martialStatus,
  driverLicenseState,
  OccupationList,
  IndustryList,
  driversLicenseType,
} from "../../common/StaticFieldList";
import { populateValue } from "../../common/helpers/utils";
import DatePickerField from "../general/DatePickerField";

const requiredFields = [
  "driverType",
  "dob",
  "firstName",
  "lastName",
  "marital",
  "relationshipToInsured",
  "gender",
];

const validationRules = [
  {
    fieldName: "driverType",
    label: "Driver Type",
    type: "select",
    option: DriverType,
    id: "drivertype",
  },
  { fieldName: "dob", label: "Date of Birth", type: "text", id: "dob" },
  {
    fieldName: "firstName",
    label: "First Name",
    type: "text",
    id: "Firstname",
  },
  { fieldName: "lastName", label: "Last Name", type: "text", id: "lastname" },
  {
    fieldName: "gender",
    label: "Gender",
    type: "select",
    option: genderList,
    id: "gender",
  },
  {
    fieldName: "relationshipToInsured",
    label: "Relationship",
    type: "select",
    option: RelationshipToInsured,
    id: "relationshiptoinsured",
  },
  {
    fieldName: "marital",
    label: "Marital Status",
    type: "select",
    option: martialStatus,
    id: "marital",
  },
  {
    fieldName: "driverLicenseType",
    label: "Driver License Type",
    type: "select",
    option: driversLicenseType,
    id: "driversLicenseType",
  },
  {
    fieldName: "driversLicenseNumber",
    label: "Driver License",
    type: "text",
    id: "driverlicensenumber",
 },

  {
    fieldName: "driversLicenseState",
    label: "Driver License State",
    type: "select",
    option: driverLicenseState,
    id: "driverlicensestate",
  },
  {
    fieldName: "driverLicenseDate",
    label: "Driver License Date",
    type: "text",
    id: "driverlicensedate",
  },
  {
    fieldName: "driverTrainingDate",
    label: "Driver Training Date",
    type: "text",
    id: "drivertrainingdate",
  },
  {
    fieldName: "dateFirstLicensedInUsa",
    label: "Years Licensed in USA",
    type: "text",
    id: "driverlicensedinusa",
  },
  {
    fieldName: "sr22",
    label: "File in type",
    type: "select",
    option: Fileintype,
    id: "sr22",
  },
  {
    fieldName: "sr22StateCaseNumber",
    label: "File in Case Number ",
    type: "number",
    id: "sr22statecasenumber",
  },
  {
    fieldName: "businessOwner",
    label: "Business Owner ",
    type: "select",
    option: CommonYesNo,
    id: "BusinessOwner",
  },
  {
    fieldName: "occupation",
    label: "Occupation",
    type: "select",
    option: OccupationList,
    id: "Occupation",
  },
  {
    fieldName: "industry",
    label: "Industry ",
    type: "select",
    option: IndustryList,
    id: "industry",
  },
  {
    fieldName: "defensiveDriving",
    label: "Accident Prevention Discount",
    type: "select",
    option: CommonYesNo,
    id: "Accidentprevention",
  },
];

const DriverSave = forwardRef(function DriverSave(
  {
    setShowHideDriverForm,
    setshowbutton,
    driverData,
    handleNext,
    id,
    rows,
    showHideDriverForm,
  },
  ref
) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const dispatch = useDispatch();
  const getGpId = useSelector((state) => state.generalPartyInfo.gpId)
  let quoteId = getGpId;

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const loading = useSelector((state) => state.loading.CREATE_USER);
  const dynamicTheme = useSelector((state) => state?.theme?.theme[0]);
  const newFieldArg = [...validationRules];
  const [formState, setFormState] = useState({
    formData: newFieldArg.reduce(
      (fields, { fieldName }) => ({
        ...fields,
        [fieldName]: "",
      }),
      {}
    ),
    touchedFields: newFieldArg.reduce(
      (fields, { fieldName }) => ({
        ...fields,
        [fieldName]: false,
      }),
      {}
    ),
    fieldErrors: newFieldArg.reduce(
      (errors, { fieldName }) => ({
        ...errors,
        [fieldName]: "",
      }),
      {}
    ),
  });

  const { formData, touchedFields, fieldErrors } = formState;

  const isDirty = () => Object.values(formState.touchedFields || {}).some(Boolean);

  const isFormValid = () => {
    const allRequiredFieldsFilled = requiredFields.every(fieldName => {
      const value = formData[fieldName];
      return value && value.toString().trim() !== "";
    });
    const noValidationErrors = requiredFields.every(fieldName => !fieldErrors[fieldName]);
    return allRequiredFieldsFilled && noValidationErrors;
  };

  const resetForm = () => {
    setFormState({
      formData: newFieldArg.reduce((fields, { fieldName }) => ({ ...fields, [fieldName]: "" }), {}),
      touchedFields: newFieldArg.reduce((fields, { fieldName }) => ({ ...fields, [fieldName]: false }), {}),
      fieldErrors: newFieldArg.reduce((errors, { fieldName }) => ({ ...errors, [fieldName]: "" }), {}),
    });
  };

  const saveWithoutNav = async () => {
    // minimal validation (same as submit but without navigation & keep toasts optional)
    const trimmed = {
      ...formData,
      firstName: formData.firstName?.trim() || "",
      lastName: formData.lastName?.trim() || "",
    };

    const validationErrors = newFieldArg.reduce((errors, { fieldName, label }) => {
      const value = trimmed[fieldName];
      let error = "";
      if (requiredFields.includes(fieldName)) {
        error = validateField(value, label);
      }
      const additionalError = validationFunctions[fieldName]
        ? validationFunctions[fieldName](value)
        : "";
      return { ...errors, [fieldName]: error || additionalError };
    }, {});

    setFormState((prev) => ({
      ...prev,
      formData: trimmed,
      fieldErrors: { ...prev.fieldErrors, ...validationErrors },
      touchedFields: Object.keys(prev.touchedFields).reduce((m, k) => ({ ...m, [k]: true }), {}),
    }));

    const hasErrors = Object.values(validationErrors).some(Boolean);
    if (hasErrors) return { ok: false };

    try {
      const formattedLicenseDate =
        trimmed.driverLicenseDate && dayjs(trimmed.driverLicenseDate, ["YYYY-MM-DD","MM-DD-YYYY","MM/DD/YYYY"], true).isValid()
          ? dayjs(trimmed.driverLicenseDate, ["YYYY-MM-DD","MM-DD-YYYY","MM/DD/YYYY"], true).format("MM-DD-YYYY")
          : "";

      const formattedTrainingDate =
        trimmed.driverTrainingDate && dayjs(trimmed.driverTrainingDate, ["YYYY-MM-DD","MM-DD-YYYY","MM/DD/YYYY"], true).isValid()
          ? dayjs(trimmed.driverTrainingDate, ["YYYY-MM-DD","MM-DD-YYYY","MM/DD/YYYY"], true).format("MM-DD-YYYY")
          : "";

      const licenseType = trimmed.driverLicenseType;
      const payload = {
        ...trimmed,
        driverLicenseDate: formattedLicenseDate || null,
        driverTrainingDate: formattedTrainingDate || null,
        gpId: id ? parseInt(id) : parseInt(quoteId),
        ...(driverData.id && { did: driverData.id }),
      };
      if (licenseType !== "Active") delete payload.driversLicenseState;

      if (driverData.id) {
        await dispatch(updateDriverInfo({ ...payload, driverNumber: driverData.driverNumber }, () => {}));
      } else {
        await dispatch(createDriverInfoAction(payload, () => {}));
      }
      return { ok: true };
    } catch {
      return { ok: false };
    }
  };

  const validationFunctions = {
    firstName: alphanumericWithSpaceValidation,
    lastName: alphanumericWithSpaceValidation,
    dob: (value) => {
      if (!value || value.trim() === "") {
        return "Date of Birth is required";
      }

      const dobDate = dayjs(value);
      const today = dayjs();
      const minDate = dayjs("1940-01-01");
      const maxDate = dayjs("2008-04-04");

      if (dobDate.isAfter(today)) {
        return "Date of Birth cannot be in the future";
      }

      if (dobDate.isBefore(minDate)) {
        return "Date of Birth must be after January 1, 1940";
      }

      if (dobDate.isAfter(maxDate)) {
        return "Date of Birth must be before April 4, 2008";
      }

      return "";
    },
    driverTrainingDate: (value) => {
      if (!value || value.trim() === "") return "";

      const trainingDate = dayjs(value);
      const dobDate = dayjs(formData.dob);

      if (!trainingDate.isValid()) {
        return "Invalid date format";
      }

      const ageAtTraining = trainingDate.diff(dobDate, 'year');
      if (ageAtTraining < 16) {
        return "Must be at least 16 years old";
      }

      return "";
    },
    driverLicenseDate: (value) => {
      if (!value || value.trim() === "") return "";

      const licenseDate = dayjs(value);
      const dobDate = dayjs(formData.dob);

      if (!licenseDate.isValid()) {
        return "Invalid date format";
      }

      const ageAtLicense = licenseDate.diff(dobDate, 'year');
      if (ageAtLicense < 16) {
        return "Must be at least 16 years old";
      }

      return "";
    }
  };

  useEffect(() => {
    if (formData.driverLicenseDate) {
      const licenseDate = dayjs(formData.driverLicenseDate, "MM-DD-YYYY");
      if (licenseDate.isValid()) {
        const yearsLicensed = dayjs().diff(licenseDate, "year");
        setFormState(prevState => ({
          ...prevState,
          formData: {
            ...prevState.formData,
            dateFirstLicensedInUsa: yearsLicensed.toString()
          }
        }));
      }
    }
  }, [formData.driverLicenseDate]);

const handleDateChange = (date, fieldName, validationFunc) => {
  let formattedDate = date ? dayjs(date).format("MM-DD-YYYY") : "";

  if (fieldName === "driverLicenseDate" && date) {
    const today = dayjs();
    const selectedDate = dayjs(date);
    if (selectedDate.isAfter(today)) {
      formattedDate = today.format("MM-DD-YYYY");
    }
  }

  const validationError = validationFunc ? validationFunc(formattedDate) : "";

  setFormState((prevState) => ({
    ...prevState,
    formData: {
      ...prevState.formData,
      [fieldName]: formattedDate,
    },
    fieldErrors: {
      ...prevState.fieldErrors,
      [fieldName]: validationError,
    },
  }));
};

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "driverType") {
      setFormState((prevState) => ({
        ...prevState,
        formData: {
          ...prevState.formData,
          [name]: value,
          relationshipToInsured:
            value === "I" ? "IN" : prevState.formData.relationshipToInsured,
        },
      }));
    }
     else if (name === "sr22") {
    setFormState((prevState) => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        [name]: value,
        // Clear sr22StateCaseNumber when "None" is selected
        sr22StateCaseNumber: value === "None" ? "" : prevState.formData.sr22StateCaseNumber,
      },
    }));
  }
     else {
      if (
        name === "relationshipToInsured" &&
        formState.formData.driverType === "I"
      ) {
        return;
      }
      setFormState((prevState) => ({
        ...prevState,
        formData: {
          ...prevState.formData,
          [name]: value,
        },
      }));
    }
  };

  const isDriverTypeInsured = (driverType) => {
    return driverType === "I";
  };

  const handleBlur = (field) => {
    setFormState((prevFormState) => ({
      ...prevFormState,
      touchedFields: {
        ...prevFormState.touchedFields,
        [field]: true,
      },
    }));

    const value = formData[field];
    let error = "";

    if (requiredFields.includes(field)) {
      error = validateField(
        value,
        newFieldArg.find((rule) => rule.fieldName === field).label
      );
    }

    const additionalError = validationFunctions[field]
      ? validationFunctions[field](value)
      : "";

    setFormState((prevFormState) => ({
      ...prevFormState,
      fieldErrors: {
        ...prevFormState.fieldErrors,
        [field]: error || additionalError,
      },
    }));
  };

  const handleSnackbarClose = () => {
    setSnackbar((prevSnackbar) => ({
      ...prevSnackbar,
      open: false,
    }));
  };

  const handleSnackbarOpen = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };
const handleSubmit = async (event) => {
    event.preventDefault();

    // Trim firstName and lastName before validation
    const trimmedFormData = {
      ...formData,
      firstName: formData.firstName?.trim() || "",
      lastName: formData.lastName?.trim() || "",
    };

    const validationErrors = newFieldArg.reduce(
      (errors, { fieldName, label }) => {
        const value = trimmedFormData[fieldName];
        let error = "";

        if (requiredFields.includes(fieldName)) {
          error = validateField(value, label);
        }

        const additionalError = validationFunctions[fieldName]
          ? validationFunctions[fieldName](value)
          : "";

        return {
          ...errors,
          [fieldName]: error || additionalError,
        };
      },
      {}
    );

    setFormState((prevState) => ({
      ...prevState,
      formData: trimmedFormData,
      fieldErrors: validationErrors,
      touchedFields: Object.keys(validationErrors).reduce(
        (fields, fieldName) => ({ ...fields, [fieldName]: true }),
        {}
      ),
    }));

    const hasErrors = Object.values(validationErrors).some((error) => !!error);

    if (!hasErrors) {
      try {
        const formattedLicenseDate = trimmedFormData.driverLicenseDate
          ? dayjs(
              trimmedFormData.driverLicenseDate,
              ["YYYY-MM-DD", "MM-DD-YYYY", "MM/DD/YYYY"],
              true
            ).isValid()
            ? dayjs(
                trimmedFormData.driverLicenseDate,
                ["YYYY-MM-DD", "MM-DD-YYYY", "MM/DD/YYYY"],
                true
              ).format("MM-DD-YYYY")
            : ""
          : "";

        const formattedTrainingDate = trimmedFormData.driverTrainingDate
          ? dayjs(
              trimmedFormData.driverTrainingDate,
              ["YYYY-MM-DD", "MM-DD-YYYY", "MM/DD/YYYY"],
              true
            ).isValid()
            ? dayjs(
                trimmedFormData.driverTrainingDate,
                ["YYYY-MM-DD", "MM-DD-YYYY", "MM/DD/YYYY"],
                true
              ).format("MM-DD-YYYY")
            : ""
          : "";
        const licenseType = trimmedFormData.driverLicenseType;
        const newFormData = {
          ...trimmedFormData,
          driverLicenseDate:
            formattedLicenseDate === "" ? null : formattedLicenseDate,
          driverTrainingDate:
            formattedTrainingDate === "" ? null : formattedTrainingDate,
          gpId: id ? parseInt(id) : parseInt(quoteId),
          ...(driverData.id && { did: driverData.id }),
        };
        if (licenseType !== "Active") {
          delete newFormData.driversLicenseState;
        }
        if (driverData.id) {
          const updatedFormData = {
            ...newFormData,
            driverNumber: driverData.driverNumber,
          };
          await dispatch(
            updateDriverInfo(updatedFormData, getCreateUserSuccess)
          );
        } else {
          await dispatch(
            createDriverInfoAction(newFormData, getCreateUserSuccess)
          );
        }
      } catch (error) {
        handleSnackbarOpen(
          error.message || "User service failed. Please try again.",
          "error"
        );
      }
    } else {
      const errorFields = Object.entries(validationErrors)
        .filter(([fieldName, error]) => !!error)
        .map(([fieldName, error]) => {
          const fieldRule = newFieldArg.find(rule => rule.fieldName === fieldName);
          return fieldRule ? fieldRule.label : fieldName;
        });

      const errorMessage = errorFields.length === 1
        ? `Fill Correct: ${errorFields[0]}`
        : `Fill Correct: ${errorFields.join(', ')}`;

      handleSnackbarOpen(errorMessage, "error");
    }
  };

  const getCreateUserSuccess = async () => {
    handleSnackbarOpen("Driver created successfully.", "success");
    setShowHideDriverForm(false);
    if (quoteId) {
      dispatch(getAllDriverInfo(quoteId));
    }
    if (id) {
      dispatch(getAllDriverInfo(id));
    }

    setFormState({
      formData: newFieldArg.reduce(
        (fields, { fieldName }) => ({
          ...fields,
          [fieldName]: "",
        }),
        {}
      ),
      touchedFields: newFieldArg.reduce(
        (fields, { fieldName }) => ({
          ...fields,
          [fieldName]: false,
        }),
        {}
      ),
      fieldErrors: newFieldArg.reduce(
        (errors, { fieldName }) => ({
          ...errors,
          [fieldName]: "",
        }),
        {}
      ),
    });
  };

  const getDefaultFieldValue = (fieldName, driverData, fieldArgs) => {
    if (fieldName === "driverType" && rows.length === 0) {
      return driverData?.[fieldName] || "I";
    }

    if (fieldName === "driverLicenseType") {
      return driverData?.[fieldName] || "Active";
    }
      if (fieldName === "defensiveDriving") {
      return driverData?.[fieldName] || "N";
    }
  if (fieldName === "sr22") {
    return driverData?.[fieldName] || "None";
  }
    if (fieldName === "relationshipToInsured" && rows.length === 0) {
      const driverType = driverData?.driverType || "I";
      if (driverType === "I") {
        return "IN";
      }
    }

    if (fieldName === "dob") {
      const rawDOB = driverData?.[fieldName];

      if (!rawDOB) {
        return "";
      }

      const parsedDate = dayjs(
        rawDOB,
        ["MM-DD-YYYY", "YYYY-MM-DD", "DD-MM-YYYY"],
        true
      );
      if (!parsedDate.isValid()) {
        return "";
      }

      const formattedDate = parsedDate.format("MM-DD-YYYY");
      return formattedDate;
    }

    return (
      (driverData &&
        populateValue(driverData[fieldName], fieldName, fieldArgs)) ||
      ""
    );
  };

  useEffect(() => {
    const updatedFormData = newFieldArg.reduce(
      (fields, { fieldName }) => ({
        ...fields,
        [fieldName]: getDefaultFieldValue(fieldName, driverData, newFieldArg),
      }),
      {}
    );

    const initialFieldErrors = {};

    setFormState((prevFormState) => ({
      ...prevFormState,
      formData: updatedFormData,
      fieldErrors: {
        ...prevFormState.fieldErrors,
        ...initialFieldErrors,
      },
      touchedFields: newFieldArg.reduce(
        (fields, { fieldName }) => ({
          ...fields,
          [fieldName]: false,
        }),
        {}
      ),
    }));
  }, [driverData]);
  useImperativeHandle(ref, () => ({
    shouldConfirm: () => isDirty() && isFormValid(),
    saveWithoutNav,
    resetForm,
  }));

  return (
    <>
      <Box
        sx={{
          alignSelf: "stretch",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: "36px",
          maxWidth: "100%",
           overflowX: 'auto',
          minHeight: '40vh',
          '@media (min-width: 768px) and (max-width: 1024px)': {
            minHeight: '120vh',
            height: 'auto',
            overflowY: 'auto',
          },
          '@media (min-width: 1024px) and (min-height: 1366px)': {
            height: 'auto',
            minHeight: '130vh',
          },

        }}
      >
        <section
          sx={{
            alignSelf: "stretch",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            padding: "0px 23px 0px 20px",
            boxSizing: "border-box",
            maxWidth: "100%",
            textAlign: "left",
            fontSize: "14px",
            color: "#242929",
            fontFamily: "Poppins",
          }}
        >
          <Box
            id="Form"
            component="form"
            onSubmit={handleSubmit}
            sx={{
              width: isMobile ? "100%" : isTablet ? "90%" : "853px",
              borderRadius: "4px",
              backgroundColor: "#fff",
              border: "1px solid #e3e3e3",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              padding: "30px 0px 24px",
              gap: "23px",
              maxWidth: "100%",
            }}
          >
            <Box
              sx={{
                alignSelf: "stretch",
                height: "798px",
                position: "relative",
                borderRadius: "4px",
                backgroundColor: "#fff",
                border: "1px solid #e3e3e3",
                boxSizing: "border-box",
                display: "none",
              }}
            />

            <Typography
              id="driverinformation"
              variant="h1"
              component="div"
              sx={{
                border: "none",
                backgroundColor: "transparent",
                alignSelf: "stretch",
                height: "51px",
                fontFamily: '"Poppins", sans-serif',
                fontWeight: "600",
                fontSize: "25px",
                color: "#242929",
                paddingLeft: isMobile ? "20px" : "50px",
                display: "flex",
                alignItems: "center",
              }}
            >
              Driver Information
            </Typography>
            <hr style={{ borderTop: "1px solid #ccc", width: "100%" }} />
            <Box
              sx={{
                alignSelf: "stretch",
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                justifyContent: "flex-end",
                padding: isMobile ? "0px 20px" : "0px 44px 0px 55px",
                boxSizing: "border-box",
                maxWidth: "100%",
                fontFamily: '"Poppins", sans-serif',
              }}
            >
              <Box
                sx={{
                  flex: "1",
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                  gap: "16px",
                  maxWidth: "100%",
                }}
              >
                <Box
                  sx={{
                    flex: "1",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    minWidth: isMobile ? "100%" : "240px",
                    maxWidth: "100%",
                  }}
                >
                  <Box
                    sx={{
                      alignSelf: "stretch",
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                      gap: "5px 20px",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      justifyContent: "flex-start",
                      padding: "0px 0px 8px",
                    }}
                  >
                    {validationRules.map(
                      ({ fieldName, label, type, option }, index) => {
                        if (
                          fieldName === "driversLicenseState" &&
                          formData.driverLicenseType !== "Active" &&
      formData.driverLicenseType !== "Suspended" &&
      formData.driverLicenseType !== "Not/Never Licensed"
                        ) {
                          return null;
                        }
                        if (
    fieldName === "sr22StateCaseNumber" &&
    (formData.sr22 === "None" || !formData.sr22)
  ) {
    return null;
  }
                        return (
                          <Box
                            key={index}
                            sx={{
                              width: "100%",
                              margin: "0 0 15px 0",
                              display: "flex",
                              flexFlow: "column",
                            }}
                          >
                            <Typography
                              key={fieldName}
                              variant="body1"
                              sx={{
                                position: "relative",
                                lineHeight: "20px",
                                fontWeight: "500",
                                display: "inline-block",
                                zIndex: "1",
                                marginBottom: "10px",
                                fontFamily: '"Poppins", sans-serif',
                              }}
                            >
                              {label}
                              {requiredFields.includes(fieldName) && (
    <span style={{ color: 'red', marginLeft: '2px' }}>*</span>
  )}
                            </Typography>
                            {fieldName === "dob" ? (
                              <DatePickerField
                                fieldName="dob"
                                formData={formData}
                                handleDateChange={handleDateChange}
                                handleBlur={handleBlur}
                                touchedFields={touchedFields}
                                fieldErrors={fieldErrors}
                                validationFunc={validationFunctions.dob}
                                size={isMobile ? "small" : "medium"}
                              />
                            ) : fieldName === "driverTrainingDate" ||
                              fieldName === "driverLicenseDate" ? (
                              <DatePickerField
                                fieldName={fieldName}
                                formData={formData}
                                handleDateChange={handleDateChange}
                                handleBlur={handleBlur}
                                touchedFields={touchedFields}
                                fieldErrors={fieldErrors}
                                validationFunc={validationFunctions[fieldName]}
                                size={isMobile ? "small" : "medium"}
                              />
                            ): fieldName === "defensiveDriving" ? (
          // Radio buttons for defensiveDriving field
          <FormControl
            component="fieldset"
            error={touchedFields[fieldName] && !!fieldErrors[fieldName]}
            sx={{
              border: "none",
              backgroundColor: "transparent",
              fontFamily: '"Poppins", sans-serif',
            }}
          >
            <RadioGroup
              row
              name={fieldName}
              value={formData[fieldName] || ""}
              onChange={(e) => handleInputChange(e)}
              sx={{
                "& .MuiRadio-root": {
                  color: "#C1C1C1",
                  "&.Mui-checked": {
                    color: dynamicTheme?.primaryColor,
                  },
                },
                "& .MuiFormControlLabel-label": {
                  fontFamily: '"Poppins", sans-serif',
                  fontSize: isMobile ? "14px" : "16px",
                  color: "#242929",
                },
              }}
            >
              {CommonYesNo.map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  value={opt.value}
                  control={<Radio size={isMobile ? "small" : "medium"} />}
                  label={opt.label}
                  sx={{
                    marginRight: "24px",
                    "& .MuiRadio-root": {
                      padding: isMobile ? "6px" : "9px",
                    },
                  }}
                />
              ))}
            </RadioGroup>
            {touchedFields[fieldName] && fieldErrors[fieldName] && (
              <FormHelperText error>
                {fieldErrors[fieldName]}
              </FormHelperText>
            )}
          </FormControl>
        ) : fieldName === "dateFirstLicensedInUsa" ? (
          <TextField
            style={{
              border: "none",
              backgroundColor: "transparent",
              alignSelf: "stretch",
              height: isMobile ? "42px" : "57px",
              fontFamily: '"Poppins", sans-serif',
              fontWeight: "500",
              fontSize: "14px",
              color: "#242929",
              zIndex: "1",
            }}
            type="text"
            variant="outlined"
            InputProps={{
              readOnly: true,
              style: {
                height: isMobile ? 42 : 55,
                fontSize: isMobile ? 14 : 16
              },
              inputProps: {
                maxLength: 50,
              },
            }}
            id={fieldName}
            placeholder={label}
            name={fieldName}
            value={formData[fieldName]}
            error={touchedFields[fieldName] && !!fieldErrors[fieldName]}
            helperText={
              touchedFields[fieldName] ? fieldErrors[fieldName] : ""
            }
            size={isMobile ? "small" : "medium"}
            sx={{
              "& fieldset": {
                borderColor: "#C1C1C1",
                fontFamily: '"Poppins", sans-serif',
              },
              "& .MuiInputBase-root": {
                height: isMobile ? "42px" : "57px",
                backgroundColor: "#f5f5f5",
                fontFamily: '"Poppins", sans-serif',
                fontSize: "14px",
              },
              "& .MuiInputBase-input": {
                color: "#242929",
                fontFamily: '"Poppins", sans-serif',
              },
            }}
          />
        ) : type === "select" ? (
                              <FormControl
                                fullWidth
                                variant="outlined"
                                sx={{
                                  border: "none",
                                  backgroundColor: "transparent",
                                  alignSelf: "stretch",
                                  fontFamily: '"Poppins", sans-serif',
                                  fontWeight: "500",
                                  fontSize: "16px",
                                  zIndex: "1",
                                }}
                                size={isMobile ? "small" : "medium"}
                              >
                                <Select
                                  native
                                  name={fieldName}
                                  value={formData[fieldName] || ""}
                                  onChange={(e) => {
                                    handleInputChange(e);
                                  }}
                                  error={
                                    touchedFields[fieldName] &&
                                    !!fieldErrors[fieldName]
                                  }
                                  displayEmpty
                                  inputProps={{ "aria-label": label }}
                                  sx={{
                                    height: isMobile ? "40px" : "55px",
                                    "& .MuiInputBase-root": {
                                      height: isMobile ? "40px" : "55px",
                                      backgroundColor: "#fff",
                                      fontSize: "14px",
                                      fontFamily: '"Poppins", sans-serif',
                                    },
                                    "& .MuiInputBase-input": {
                                      color: dynamicTheme?.secondaryColor,
                                      fontFamily: '"Poppins", sans-serif',
                                    },
                                    "& fieldset": {
                                      borderColor:
                                        touchedFields[fieldName] &&
                                        !!fieldErrors[fieldName]
                                          ? "#d32f2f"
                                          : "#C1C1C1",
                                      fontFamily: '"Poppins", sans-serif',
                                    },
                                  }}
                                  disabled={
                                    fieldName === "relationshipToInsured" &&
                                    isDriverTypeInsured(
                                      formState.formData.driverType
                                    )
                                  }
                                >
                                  <option value="" disabled>
                                    Select {label}
                                  </option>
                                  {option &&
                                    option.map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </option>
                                    ))}
                                </Select>
                                {touchedFields[fieldName] &&
                                  fieldErrors[fieldName] && (
                                    <FormHelperText error>
                                      {fieldErrors[fieldName]}
                                    </FormHelperText>
                                  )}
                              </FormControl>
                            ) : (
                              <TextField
                                style={{
                                  border: "none",
                                  backgroundColor: "transparent",
                                  alignSelf: "stretch",
                                  height: fieldName === "driversLicenseNumber"
                                    ? (isMobile ? "42px" : "57px")
                                    : (isMobile ? "40px" : "55px"),
                                  fontFamily: '"Poppins", sans-serif',
                                  fontWeight: "500",
                                  fontSize: "14px",
                                  color: "#242929",
                                  zIndex: "1",
                                }}
                                type={type}
                                variant="outlined"
                                select={type === "select"}
                                InputProps={{
                                  style: {
                                    height: fieldName === "driversLicenseNumber"
                                      ? (isMobile ? 42 : 55)
                                      : (isMobile ? 40 : 52),
                                    fontSize: isMobile ? 14 : 16
                                  },
                                  inputProps: {
                                    min: type === "number" ? "0" : "",
                                    maxLength: 50,
                                  },
                                  readOnly:
                                    fieldName === "relationshipToInsured" &&
                                    isDriverTypeInsured(
                                      formState.formData.driverType
                                    ),
                                }}
                                required={requiredFields.includes(fieldName)}
                                id={fieldName}
                                placeholder={label}
                                name={fieldName}
                                autoComplete={fieldName}
                                value={formData[fieldName]}
                                onChange={(e) => {
                                  if (
                                    type === "number" &&
                                    e.target.value.length > 10
                                  ) {
                                    return;
                                  }
                                  if (
                                    (fieldName === "firstName" ||
                                      fieldName === "lastName" ||
                                      fieldName === "sr22StateCaseNumber" ||
                                      fieldName === "driversLicenseNumber") &&
                                    !/^[A-Za-z0-9\s]*$/.test(e.target.value)
                                  ) {
                                    return;
                                  }
                                  handleInputChange(e);
                                }}
                                error={
                                  touchedFields[fieldName] &&
                                  !!fieldErrors[fieldName]
                                }
                                helperText={
                                  touchedFields[fieldName]
                                    ? fieldErrors[fieldName]
                                    : ""
                                }
                                disabled={
                                  fieldName === "relationshipToInsured" &&
                                  isDriverTypeInsured(
                                    formState.formData.driverType
                                  )
                                }
                                size={isMobile ? "small" : "medium"}
                                sx={{
                                  "& fieldset": {
                                    borderColor: "#C1C1C1",
                                    fontFamily: '"Poppins", sans-serif',
                                  },
                                  "& .MuiInputBase-root": {
                                    height: fieldName === "driversLicenseNumber"
                                      ? (isMobile ? "42px" : "57px")
                                      : (isMobile ? "40px" : "55px"),
                                    backgroundColor: "#fff",
                                    fontFamily: '"Poppins", sans-serif',
                                    fontSize: "14px",
                                    "&.Mui-disabled": {
                                      backgroundColor: "#f5f5f5",
                                      "& fieldset": {
                                        borderColor: "#C1C1C1 !important",
                                      },
                                    },
                                  },
                                  "& .MuiInputBase-input": {
                                    color: "#242929",
                                    fontFamily: '"Poppins", sans-serif',
                                  },
                                }}
                              ></TextField>
                            )}
                          </Box>
                        );
                      }
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box sx={{
              display: "flex",
              justifyContent: "flex-end"
            }}>
              <Button
                id="Cancel-button"
                variant='outlined'
                sx={{mr:2}}
                onClick={() => setShowHideDriverForm(false)}
              >
                Cancel
              </Button>
              <Button
                id="savebutton"
                type="submit"
                disabled={!isFormValid()}
                variant="outlined"
              >
                {loading ? (
                  <CircleOutlined size="24" color="secondary" />
                ) : (
                  "Save"
                )}
              </Button>
            </Box>
          </Box>
          {!setshowbutton && !showHideDriverForm && (
            <>
              <div
                style={{
                  alignSelf: "stretch",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  padding: "20px 20px 50px 23px",
                }}
              >
                <Button
                  id="continuebutton"
                  disableElevation={true}
                  variant="contained"
                  onClick={handleNext}
                  size={isMobile ? "small" : "medium"}
                >
                  Continue
                </Button>
              </div>
            </>
          )}
          <Snackbar
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
          >
            <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </section>
      </Box>
    </>
  );
});

export default DriverSave;